import React, { Fragment, useState, useEffect, useRef } from "react";
import {
  Button,
  Typography,
  CircularProgress,
  Tooltip,
  ClickAwayListener,
  Backdrop,
} from "@mui/material";
import classNames from "classnames";
import moment from "moment";
import Base from "common/constants";
import { LOG_MODULES, LOG_CODE_PREFIX, trackException } from "common/errorTracker";

const MailProApprovalSection = React.memo(
  ({
    isApprovalLoading,      // true while the approve API call is in flight → shows spinner
    isDataLoading,  // true while getMailerProDetails is loading → gates the timer
    enableMailProApprovalProcess,
    mailProStatus,
    readyToMail,
    currentArea,
    mlsAreas,
    agentID,
    isAgentPaused,
    classes,
    popoverRef,
    onApproveClick,
  }) => {
    // true  → shows "Please Wait..." placeholder while the 2s grace timer runs
    // false → APPROVE button becomes visible and clickable
    const [checkingApprovalButtonStatus, setCheckingApprovalButtonStatus] = useState(true);

    // Drives the backdrop + tooltip that appears on top of the APPROVE button
    // on first load. Agent dismisses it by clicking "Continue Editing" or the
    // button itself. Resets to true whenever the agent switches to a new area.
    const [showApprovalTooltip, setShowApprovalTooltip] = useState(true);

    const timerRef = useRef(null);

    // Tracks previous canApprove to detect late-arrival of the approval flag.
    const prevCanApproveRef = useRef(false);

    const isAreaPaused = (currentArea || {}).isAreaPaused;
    const mlsAreaID = (currentArea || {}).mlsAreaID;

    // Single source of truth for "is the approve button allowed to show".
    // All three conditions must be true:
    //   enableMailProApprovalProcess → admin opened the approval window (from agentData.enableApprovalButton)
    //   !isAreaPaused                → this specific area is not on hold
    //   !isAgentPaused               → the agent's account is not on hold
    const canApprove = enableMailProApprovalProcess && !isAreaPaused && !isAgentPaused;

    // Drive the grace timer from isApprovalLoading rather than a fixed timeout tied to
    // mlsAreaID. This guarantees the button never appears before the API response
    // is fully in Redux — regardless of how long the request takes.
    //
    // State machine:
    //   !canApprove           → always "Please Wait..." (paused / window closed)
    //   canApprove + loading  → "Please Wait..." (data still in flight)
    //   canApprove + loaded   → 1s grace, then show button
    useEffect(() => {
      const wasEnabled = prevCanApproveRef.current;
      prevCanApproveRef.current = canApprove;

      if (!canApprove) {
        clearTimeout(timerRef.current);
        setCheckingApprovalButtonStatus(true);
        return;
      }

      if (isDataLoading) {
        // Area switch or fresh load in progress — keep button hidden.
        clearTimeout(timerRef.current);
        setCheckingApprovalButtonStatus(true);
        return;
      }

      // canApprove=true + isApprovalLoading=false: data is ready.
      // Log if the approval flag arrived late (agentData loaded after propertyData).
      if (!wasEnabled) {
        trackException({
          module: LOG_MODULES.MAILPRO,
          code: `${LOG_CODE_PREFIX.MAILPRO}APPROVAL_BUTTON_LATE_ENABLE`,
          error: new Error('Approval flag arrived after data loaded — button would have stayed hidden without recovery'),
          level: 'warning',
          data: { mlsAreaID },
        });
      }

      // Short grace so the table finishes rendering before the button is live.
      clearTimeout(timerRef.current);
      setCheckingApprovalButtonStatus(true);
      timerRef.current = setTimeout(() => setCheckingApprovalButtonStatus(false), 1000);

      return () => clearTimeout(timerRef.current);
    }, [canApprove, isDataLoading]);

    // Reset the onboarding tooltip each time the agent navigates to a new area.
    useEffect(() => {
      setShowApprovalTooltip(true);
    }, [mlsAreaID]);

    const closeTooltip = () => setShowApprovalTooltip(false);

    const handleApproveButtonClick = (event) => {
      event.currentTarget.style.animation = 0;
      // Close tooltip AFTER opening the dialog — avoids the visual jerk caused
      // by the backdrop animating out while the approval dialog animates in.
      onApproveClick();
      closeTooltip();
    };

    const areaName = currentArea && currentArea.mlsNeighborhood;

    // API call in progress after agent clicked APPROVE — show spinner.
    if (isApprovalLoading) {
      return (
        <div className={classes.headerLoader}>
          <CircularProgress />
        </div>
      );
    }

    // ── Pause checks ────────────────────────────────────────────────────────
    // These are checked BEFORE enableMailProApprovalProcess because an agent
    // can have enableApprovalButton:true on their profile while simultaneously
    // being paused at account or area level. Without these early returns, the
    // component would fall through to "Please Wait..." and never resolve
    // (canApprove=false means the 2s timer never fires).
    //
    // Priority order:
    //   1. isAgentPaused  — whole account on hold (admin-set on agentData)
    //   2. isAreaPaused   — only this area on hold (set per area in navbarInfo)
    //   3. !enableMailProApprovalProcess — approval window not yet open
    //      (driven by agentData.enableApprovalButton, toggled by admin per cycle)
    if (isAgentPaused) {
      return (
        <div className={classes.blockedHeaderNotice}>
          <Typography variant="body2" className={classNames(classes.whiteColor)}>
            Your account is temporarily on hold. We're taking care of a few
            things on our end and will have you back up shortly. If you have any
            questions, please don't hesitate to reach out via the Support button.
          </Typography>
        </div>
      );
    }

    if (isAreaPaused) {
      return (
        <div className={classes.blockedHeaderNotice}>
          <Typography variant="body2" className={classNames(classes.whiteColor)}>
            Approval for this area is temporarily on hold while we get everything
            ready for you. We appreciate your patience — please reach out via the
            Support button if you have any questions.
          </Typography>
        </div>
      );
    }

    if (!enableMailProApprovalProcess) {
      return (
        <div className={classes.blockedHeaderNotice}>
          <Typography variant="body2" className={classNames(classes.whiteColor)}>
            The approval window is not open at this time. Once it opens, you'll
            be able to approve your mailing right here. If you have any questions
            in the meantime, please reach out via the Support button.
          </Typography>
        </div>
      );
    }

    // ── Approval flow states (only reached when canApprove conditions are met) ─

    // All areas done: either backend confirmed allAreasApproved, or
    // readyToMailExceptPausedAreas() returns true (every non-paused area is Approved).
    // Shows success message + View Estimated Invoice button.
    if (mailProStatus === "allAreasApproved" || readyToMail) {
      return (
        <div className={classes.blockedHeaderSuccess}>
          <Typography
            variant="body2"
            className={classNames(classes.whiteColor, classes.blockMarginRight)}
          >
            <p>Thank you! All your areas have been approved.</p>
            <p>When your PMAs are being printed, MailPro is temporarily locked.</p>
          </Typography>
          <a
            href={`${Base.APP_URLS.API_BASE}/provisionalInvoice/${agentID}/${moment(new Date()).format("DD")}/${moment(new Date()).format("MM")}/${moment(new Date()).format("YYYY")}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button
              size="small"
              variant="contained"
              className={classNames(classes.marginLeftViewInvoice)}
              color="primary"
            >
              View Estimated Invoice
            </Button>
          </a>
        </div>
      );
    }

    // This specific area was already approved (agent may have switched back to it
    // after approving it earlier, or page refreshed mid-session).
    if ((currentArea || {}).mailingStatus === "Approved") {
      return (
        <div>
          <Typography variant="subtitle2" className={classes.alreadyApproved}>
            Area {areaName} has been Approved
          </Typography>
        </div>
      );
    }

    // 2-second grace period after canApprove becomes true. Keeps the header
    // height stable while the navbar/table finishes settling after load or
    // area switch. Once the timer fires, this becomes false and the button renders.
    if (checkingApprovalButtonStatus) {
      return (
        <Typography variant="subtitle1" className={classes.approvalPleaseWait}>
          Please Wait ....
        </Typography>
      );
    }

    // Tooltip + backdrop that guides the agent to click APPROVE on first load.
    // showApprovalTooltip resets to true on each area change (see useEffect above).
    // Agent dismisses it via "Continue Editing" or by clicking the APPROVE button.
    const tooltipOpen = enableMailProApprovalProcess && showApprovalTooltip;

    return (
      <Fragment>
        <Backdrop className={classes.approvalBackdrop} open={tooltipOpen} />
        <ClickAwayListener onClickAway={() => {}}>
          <Tooltip
            arrow
            PopperProps={{ disablePortal: true }}
            open={tooltipOpen}
            placement="bottom"
            classes={{
              arrow: classes.tooltipArrowMain,
              tooltip: classes.approvalTooltipMain,
              tooltipArrow: classes.tooltipArrow,
            }}
            title={
              <Fragment>
                <p>
                  Please click on the above green{" "}
                  <span className={classes.approvalGreenBg}>APPROVE</span> tab
                  if you've finished your Global Note and/or any Personal Notes
                  and/or any Changes for Area
                </p>
                <p>{areaName}.</p>
                <br />
                {Array.isArray(mlsAreas) && mlsAreas.length > 1 && (
                  <Fragment>
                    <br />
                    <p>
                      Please click the{" "}
                      <span className={classes.approvalGreenBg}>APPROVE</span>{" "}
                      button for each of your areas.
                    </p>
                  </Fragment>
                )}
                <div className={classes.approvalButtonContainer}>
                  <Button
                    variant="outlined"
                    size="small"
                    color="default"
                    onClick={closeTooltip}
                    className={classes.approvalButton}
                  >
                    Continue Editing
                  </Button>
                </div>
              </Fragment>
            }
          >
            <div className={tooltipOpen ? classes.approvalBackdropHighlight : ""}>
              <Button
                variant="contained"
                className={classNames(classes.approveMailingList)}
                onClick={handleApproveButtonClick}
                ref={popoverRef}
                disabled={checkingApprovalButtonStatus}
              >
                Click to Approve the {moment(new Date()).format("MMMM")} Mailing
                for This Area
              </Button>
            </div>
          </Tooltip>
        </ClickAwayListener>
      </Fragment>
    );
  }
);

MailProApprovalSection.displayName = "MailProApprovalSection";

export default MailProApprovalSection;
